import {
  useState, useEffect, useRef,
  useMemo,
} from 'react';
import { Helmet } from 'react-helmet';
import { useParams } from 'react-router';
import { CSVLink } from 'react-csv';
import { toast } from 'react-toastify';
import { Flex, Box } from 'reflexbox';
import { useHistory, useLocation } from 'react-router-dom';

import {
  Loader, ActionButton, RadioButton, Button, ModalWindow, Card,
} from 'components';
import { formatHealthIcon, labelDescHealth } from 'components/HealthIcon';
import { TextArea } from '~/components/NewInputs/TextArea';
import { getCachedDevInfo, getCachedDevInfoSync } from 'helpers/cachedStorage';
import { getUserProfile } from 'helpers/userProfile';
import { useStateVar } from 'helpers/useStateVar';
import {
  DeleteOutlineIcon,
  AcceptIcon,
  AnnotationIcon,
} from 'icons';

import { approveOrRejectSelectedPendingFaults } from '~/pages/Admin/FaultsList';
import { ApiResps, apiCall, apiCallFormDataMultFiles } from 'providers';
import { colors } from 'styles/colors';
import { useTranslation } from 'react-i18next';

import {
  SelectedStatusText,
  StatusText,
  StyledBox,
  Icon,
  CardWrapper,
  CardTitle,
  GreenButton,
  YellowButton,
  OrangeButton,
  RedButton,
  GreyButton,
  DarkGreyButton,
  SaveButton,
  TContainer,
  THeader,
  TableHead,
  TableBody,
  HeaderTitle,
  Data,
  Row,
  BtnExport,
  ExportWorksheet,
  Text,
  ButtonCustom,
  ContainerHealths,
} from './styles';
import styled from 'styled-components';
import { generateNameFormatted } from '~/helpers/titleHelper';
import moment, { Moment } from 'moment';
import { formatPhone } from '~/helpers/formatPhone';
import { HealthHistoryTable } from '~/components/HealthsTable/healthHistoryTable';
import { ObservationTable } from '~/components/HealthsTable/observationTable';
import { FeedbackModal } from './FeedbackModal';

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

function isViewableImage(url: string): Promise<boolean> {
  const path = url.split('?')[0].toLowerCase();
  if (path.endsWith('.pdf')) return Promise.resolve(false);
  if (['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp'].some((ext) => path.endsWith(ext))) return Promise.resolve(true);
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;
  });
}

export interface ObservationInfo {
  devId: string;
  date: string;
  OBS_DESC: string;
  userId: string;
  observationId: string;
}

type ImagePreview = {
  file: Blob;
  preview: string;
};

type PdfPreview = {
  file: Blob;
  preview: string;
  name: string;
};

export const Health = (): JSX.Element => {
  const query = useQuery();
  const location = useLocation();
  const { devId } = useParams<{ devId: string }>();
  const [profile] = useState(() => getUserProfile());
  const [descObs, setDescObs] = useState<string>();
  const csvLinkEl = useRef<CSVLink & HTMLAnchorElement & { link: HTMLAnchorElement }>(null);
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [maintenance, setMaintenance] = useState(false);
  const [images, setImages] = useState<ImagePreview[]>([]);
  const [pdfs, setPdfs] = useState<PdfPreview[]>([]);
  const [state, render, setState] = useStateVar({
    selectedDate: null as Moment | null,
    isFocusedDate: false,
    tomorrow: moment(moment().add(1, 'days').format('YYYY-MM-DD')),
    devInfo: getCachedDevInfoSync(devId),
    feedbackInfo: {} as {
      ID: number;
      ASSET_HEALTH_HIST_ID: number;
      REASON: string;
      USER_NAME: string;
      PHONE_NUMBER?: string;
      MAINTENANCE_DONE?: number;
      MAINTENANCE_DATE?: string;
      ASSET_ID: number;
      IMAGES?: string[];
    },
    healthIndexes: {},
    currentHealthIndex: null as null | number,
    currentHealthTitle: null as null | string,
    currentLaudo: null as null | string,
    currentPosCauses: [] as string[],
    selectedLevelTitle: null as null | string,
    isLoading: false,
    isLoadingModal: false,
    newHealthIndex: null as null | number,
    showAddObservation: false as boolean,
    newLaudo: null as null | string,
    detectedFaults: [] as { origin: string; faultId: string; faultDesc: string; severity: string; faultLevel: number; actionAprove: boolean; actionTaken?: string; origem: string; lastAction: string }[],
    healthHist: [] as { devId: string; assetId: number; date: string; DAT_REPORT: number; healthIndex: string; H_INDEX: number; possCauses: string[]; UNIT_ID: number; desc: string; changeType: string; }[],
    observations: [] as { devId: string; date: string; OBS_DESC: string; userId: string; observationId: string; }[],
    modalEditObservation: null as null | {
      addEdit: 'Add' | 'Edit'
      observationId: string
    },
    currIdHealth: null as null | number,
    laudos: [] as {
      text: string;
      application: string[];
      pcauses?: string[];
      fullText?: string;
    }[],
    csvData: [] as {}[],
    assetLayout: false as boolean,
    reason: null as null|string,
    phone: null as null|string,
  });

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;

    Array.from(e.target.files).forEach((file) => {
      const preview = URL.createObjectURL(file);
      if (file.type === 'application/pdf') {
        setPdfs((prev) => prev.concat({ file, preview, name: file.name }));
      } else {
        setImages((prev) => prev.concat({ file, preview }));
      }
    });
  };

  const removeImage = (index: number) => {
    setImages((prev) => {
      const newImages = [...prev];
      const [removed] = newImages.splice(index, 1);
      URL.revokeObjectURL(removed.preview);
      return newImages;
    });
  };

  const removePdf = (index: number) => {
    setPdfs((prev) => {
      const newPdfs = [...prev];
      const [removed] = newPdfs.splice(index, 1);
      URL.revokeObjectURL(removed.preview);
      return newPdfs;
    });
  };

  const CSVheader = [
    { label: t('dispositivo'), key: 'id' },
    { label: t('status'), key: 'status' },
    { label: t('descricao'), key: 'descricao' },
    { label: t('data'), key: 'data' },
    { label: t('causa'), key: 'causa' },
    { label: t('tipoMudanca'), key: 'tipo' },
  ];

  const handleModalClose = () => {
    if (state.feedbackInfo) {
      const params = new URLSearchParams(location.search);
      params.delete('modal');
      history.replace({ search: params.toString() });
      return setIsModalOpen(false);
    }
    setIsModalOpen(false);
    setImages([]);
    images.forEach((img) => URL.revokeObjectURL(img.preview));
    setPdfs([]);
    pdfs.forEach((pdf) => URL.revokeObjectURL(pdf.preview));
    setMaintenance(false);
    setState({
      selectedDate: null,
      reason: null,
      phone: null,
    });
    render();

    const params = new URLSearchParams(location.search);
    params.delete('modal');
    history.replace({ search: params.toString() });
  };

  const handleModalOpen = () => {
    setIsModalOpen(true);
    render();

    const params = new URLSearchParams(location.search);
    params.set('modal', 'true');
    history.replace({ search: params.toString() });
  };

  useEffect(() => {
    if (query.get('modal') === 'true') {
      setIsModalOpen(true);
      render();
    }
  }, [location.search]);

  async function submitFeedback() {
    try {
      if (state.currIdHealth && state.reason) {
        setState({ isLoadingModal: true });
        await apiCallFormDataMultFiles('/asset/save-maintainer-feedback', {
          asset_health_hist_id: state.currIdHealth,
          reason: state.reason,
          phone_number: state.phone || undefined,
          maintenance_done: maintenance === true ? 1 : 0,
          maintenance_date: state.selectedDate
            ? state.selectedDate.format('YYYY-MM-DD HH:mm:ss')
            : undefined,
          client_id: state.devInfo.CLIENT_ID,
          unit_id: state.devInfo.UNIT_ID,
        }, {
          files: (() => {
            const all = [...images.map((img) => img.file), ...pdfs.map((pdf) => pdf.file)];
            return all.length > 0 ? all : null;
          })(),
        });
        handleModalClose();
        setState({ isLoadingModal: false });
        toast.success(t('sucessoEnviarFeedbackSaude'));
        window.location.reload();
      } else {
        toast.error(t('erroEnviarFeedbackSaude'));
      }
    } catch (err) {
      console.log(err);
      setState({ isLoadingModal: false });
      toast.error(t('erroEnviarFeedbackSaude'));
    }
  }

  const history = useHistory();
  const linkBase = history.location.pathname;
  state.assetLayout = linkBase.includes('/ativo');

  function filterLaudo(laudos: typeof state.laudos, selector?: string) {
    return laudos.filter((item) => {
      if (!item.application) return true;
      if (!selector) return true;
      if (selector === 'DUO') return true; // provisoriamente, fazemos aparecer todos os laudos para DUT duo.
      return item.application.includes(selector);
    });
  }

  function buildLaudoFullText(laudo: typeof state.laudos[number], pcauses: { [k: string]: { text: string } }) {
    laudo.fullText = laudo.text;
    if (!laudo.pcauses) return laudo;
    const causesDesc = laudo.pcauses.map((id) => (pcauses?.[id]?.text)).filter((x) => !!x);
    if (causesDesc.length === 0) return laudo;
    laudo.fullText += ` (${causesDesc.join(', ')})`;
    return laudo;
  }

  function buildDetectedFault(
    faultData: {
      origin: string,
      id: string,
      faultName: string,
      faultLevel: number,
      lastAction: string,
      lastActionTime: number
    },
    healthIndexes: { [k: string]: { titulo: string } },
  ) {
    const fdata = {
      origin: faultData.origin,
      faultId: faultData.id,
      faultDesc: faultData.faultName,
      severity: (healthIndexes[String(faultData.faultLevel)] || {}).titulo || '?',
      faultLevel: faultData.faultLevel,
      actionAprove: true,
      actionTaken: undefined as string | undefined,
      origem: faultData.origin,
      lastAction: faultData.lastAction,
    };
    if (faultData.lastActionTime) {
      const actionTime = new Date(faultData.lastActionTime);
      const approvableActions: string[] = ['PENDING', 'RESTAB_PENDING'];
      if (!approvableActions.includes(faultData.lastAction)) {
        fdata.actionTaken = `${faultData.lastAction} ${actionTime.toLocaleString()}`;
        fdata.actionAprove = false;
      }
    }
    return fdata;
  }

  async function verifyHealthStatus(healthStatus: ApiResps['/asset/get-health-status']['healthStatus'], healthIndexes: ApiResps['/faults/get-fault-codes']['healthIndexes']) {
    if (healthStatus) {
      state.detectedFaults = [];
      if (healthStatus.fdetected) {
        healthStatus.fdetected.forEach((faultData) => {
          const fdata = buildDetectedFault(faultData, healthIndexes);
          state.detectedFaults.push(fdata);
        });
      }
    }
  }

  async function handleGetDevHealth() {
    try {
      setState({ isLoading: true });

      const devInfo = await getCachedDevInfo(devId);
      state.devInfo = devInfo;

      if (devInfo?.ASSET_ID) {
        const [{ possibleCauses, healthIndexes, laudos },
          { list: healthList },
          { healthStatus }] = await Promise.all([
          apiCall('/faults/get-fault-codes', {}),
          apiCall('/asset/get-health-hist', { assetId: devInfo.ASSET_ID }),
          apiCall('/asset/get-health-status', { ASSET_ID: devInfo.ASSET_ID }),
        ]);

        const assetTypeSelector = devInfo?.dac?.DAC_APPL ?? devInfo?.dut?.PLACEMENT;

        state.healthIndexes = (healthIndexes || {});
        state.healthHist = (healthList || []);
        state.laudos = (laudos || []);
        state.currIdHealth = healthStatus.ASSET_HEALTH_HIST_ID;
        state.currentHealthIndex = healthStatus.H_INDEX;
        state.currentHealthTitle = ((state.healthIndexes || {})[String(healthStatus.H_INDEX)] || {}).titulo;
        state.currentLaudo = healthStatus.H_DESC;
        state.currentPosCauses = (healthStatus.P_CAUSES || '')
          .split(',')
          .map((id) => (possibleCauses?.[id]?.text))
          .filter((x) => !!x);
        for (const laudo of state.laudos) {
          buildLaudoFullText(laudo, possibleCauses);
        }

        verifyHealthStatus(healthStatus, healthIndexes);

        state.laudos = filterLaudo(state.laudos, assetTypeSelector);

        const feedback = await apiCall('/asset/get-maintainer-feedback-by-user', {
          asset_health_hist_id: healthStatus.ASSET_HEALTH_HIST_ID,
          client_id: devInfo.CLIENT_ID,
          unit_id: devInfo.UNIT_ID,
        });

        setState({ feedbackInfo: feedback });
        if (feedback) {
          setMaintenance(feedback.MAINTENANCE_DONE === 1);
          setState({
            phone: feedback.PHONE_NUMBER ?? null,
            reason: feedback.REASON,
            selectedDate: feedback.MAINTENANCE_DATE ? moment(feedback.MAINTENANCE_DATE) : null,
          });

          const allFiles = feedback.IMAGES ?? [];
          const imageTests = await Promise.all(allFiles.map(isViewableImage));
          setImages(allFiles.filter((_, i) => imageTests[i]).map((url) => ({ file: new Blob(), preview: url })));
          setPdfs(allFiles.filter((_, i) => !imageTests[i]).map((url) => ({
            file: new Blob(),
            preview: url,
            name: decodeURIComponent((url.split('?')[0].split('/').pop()) ?? 'documento.pdf'),
          })));
        }
      }
      render();
    } catch (err) {
      console.log(err);
      toast.error(t('erroSaudeMaquinas'));
    }
    setState({ isLoading: false });
  }

  async function saveHealthInfo() {
    try {
      if (!state.devInfo.ASSET_ID) {
        toast.warn(t('erroSalvarSaudeSemAtivoRelacionado'));
        return;
      }
      const selectedLaudo = (state.newLaudo && state.laudos.find((x) => (x.fullText === state.newLaudo))) || null;
      if ((!selectedLaudo) && (state.newHealthIndex !== 100)) {
        toast.error(t('errorSelecioneLaudo'));
        return;
      }
      if (state.newHealthIndex == null) {
        toast.error(t('erroIndiceSaude'));
        return;
      }
      await apiCall('/asset/save-health-info', {
        assetId: state.devInfo.ASSET_ID,
        healthIndex: state.newHealthIndex,
        possibleCauses: selectedLaudo && selectedLaudo.pcauses,
        laudo: selectedLaudo && selectedLaudo.text,
      });
      toast.success(t('sucessoSalvar'));
      state.newHealthIndex = null;
      state.newLaudo = null;
      render();
      await handleGetDevHealth();
      handleModalClose();
    } catch (err) {
      console.log(err);
      toast.error(t('erro'));
    }
  }

  const getCsvData = async () => {
    state.isLoading = true; render();
    const formattedCSV = [] as any;
    try {
      if (state.healthHist) {
        state.healthHist.forEach((item) => {
          formattedCSV.push({
            id: item.devId,
            status: labelDescHealth(item.H_INDEX),
            descricao: item.desc,
            data: new Date(item.date).toLocaleString('pt-BR'),
            causa: (item.possCauses && (item.possCauses.length > 0)) && (
              item.possCauses.map((cause) => `• ${cause}\n`).join('')
            ),
            tipo: item.changeType,
          });
        });

        state.csvData = formattedCSV;
        render();
        setTimeout(() => {
          csvLinkEl.current?.link.click();
        }, 1000);

        state.isLoading = false; render();
      }
      else {
        toast.info(t('naoHaDadosSaudeParaExportar')); state.isLoading = false;
      }
    } catch (err) { console.log(err); toast.error(t('houveErro')); state.isLoading = false; }
  };

  async function wantDeleteHistoryEvent(item: {
    devId: string;
    assetId: number;
    date: string;
    DAT_REPORT: number;
    desc: string;
    changeType: string;
    healthIndex: string;
    possCauses: string[];
    UNIT_ID: number;
    H_INDEX: number;
  }) {
    if (window.confirm(`${t('desejaExcluir')} "${item.desc}" [${item.changeType}]?`)) {
      await apiCall('/asset/delete-health-hist', {
        assetId: item.assetId,
        itemDate: item.DAT_REPORT,
        healthIndex: item.H_INDEX,
      });
      window.location.reload();
    }
  }

  async function getObservations() {
    if ((profile.manageAllClients || profile.permissions.isInstaller) && state.devInfo?.ASSET_ID) {
      const { list: observationsList } = await apiCall('/asset/get-observation', { assetId: state.devInfo.ASSET_ID });
      setState({
        observations: (observationsList || []),
      });
    }
  }

  useEffect(() => {
    handleGetDevHealth();
  }, []);

  useMemo(async () => {
    getObservations();
  }, []);

  function onLevelClick(newHealthIndex: number) {
    const selectedLevelTitle = ((state.healthIndexes || {})[String(newHealthIndex)] || {}).titulo;
    if (newHealthIndex === 100) state.newLaudo = null;
    setState({ newHealthIndex, selectedLevelTitle });
  }

  async function wantDeleteObservation(item: ObservationInfo) {
    if (profile.manageAllClients || profile.permissions.isInstaller) {
      try {
        if (window.confirm(`${t('desejaExcluir')} "${item.OBS_DESC}"?`)) {
          const itemDate = Math.round(new Date(item.date).getTime() / 1000);
          await apiCall('/asset/delete-observation', {
            assetId: state.devInfo.ASSET_ID,
            itemDate,
          });
          toast.success(t('observacaoExcluida'));
          window.location.reload();
        }
      } catch (err) { console.log(err); toast.error(t('houveErro')); }
    }
  }

  function wantEditAddProgramming(item?: ObservationInfo) {
    try {
      state.modalEditObservation = {
        addEdit: item ? 'Edit' : 'Add',
        observationId: (item && item.observationId) || '',
      };
      setDescObs((item && item.OBS_DESC) || '');
      render();
    } catch (err) { console.log(err); toast.error(t('houveErro')); }
  }

  async function saveNewObservation() {
    if (!state.devInfo.ASSET_ID) {
      toast.warn(t('erroAdicionarObservacaoSemAtivoRelacionado'));
    } else if (profile.manageAllClients || profile.permissions.isInstaller) {
      try {
        await apiCall('/asset/save-observation-info', {
          assetId: state.devInfo.ASSET_ID,
          observationDesc: descObs,
        });
        state.modalEditObservation = null;
        toast.success(t('sucessoSalvar'));
        window.location.reload();
      } catch (err) {
        console.log(err);
        toast.error('Erro');
      }
    }
  }

  async function editObservation() {
    if (profile.manageAllClients || profile.permissions.isInstaller) {
      try {
        await apiCall('/asset/edit-observation-info', {
          observationId: state.modalEditObservation!.observationId,
          observationDesc: descObs,
        });
        state.modalEditObservation = null;
        toast.success(t('sucessoSalvar'));
        window.location.reload();
      } catch (err) {
        console.log(err);
        toast.error('Erro');
      }
    }
  }

  function showAddObservation() {
    try {
      state.showAddObservation = true;
      render();
    } catch (err) { console.log(err); toast.error(t('houveErro')); }
  }

  const isDesktop = window.matchMedia('(min-width: 1024px)').matches;

  return (
    <>
      <Helmet>
        <title>{generateNameFormatted(state.devInfo?.DEV_ID, t('saude'))}</title>
      </Helmet>
      {(state.isLoading)
        && (
          <Flex alignItems="center" justifyContent="center" mt="32px">
            <Box width={1} alignItems="center" justifyContent="center">
              <Loader />
            </Box>
          </Flex>
        )}
      {(!state.isLoading)
        && (
          <Flex flexDirection="column">
            <Box mt="32px" width="100%">
              <CardWrapper style={{ display: 'flex', flexDirection: isDesktop ? 'row' : 'column' }}>
                <ContainerHealths>
                  <Flex>
                    <StyledBox health={25} selected={state.currentHealthIndex === 25}>
                      <Icon>
                        {formatHealthIcon(25)}
                      </Icon>
                      <SelectedStatusText health={state.currentHealthIndex || 0}>{state.currentHealthTitle}</SelectedStatusText>
                    </StyledBox>
                    <StyledBox health={50} selected={state.currentHealthIndex === 50}>
                      <Icon>
                        {formatHealthIcon(50)}
                      </Icon>
                    </StyledBox>
                    <StyledBox health={75} selected={state.currentHealthIndex === 75}>
                      <Icon>
                        {formatHealthIcon(75)}
                      </Icon>
                    </StyledBox>
                    <StyledBox health={100} selected={state.currentHealthIndex === 100}>
                      <Icon>
                        {formatHealthIcon(100)}
                      </Icon>
                    </StyledBox>
                    <StyledBox health={0} selected={![4, 25, 50, 75, 100].includes(state.currentHealthIndex!)}>
                      <Icon>
                        {formatHealthIcon(0)}
                      </Icon>
                    </StyledBox>
                    <StyledBox health={4} selected={state.currentHealthIndex === 4}>
                      <Icon>
                        {formatHealthIcon(4)}
                      </Icon>
                    </StyledBox>
                  </Flex>
                  <Flex mt="16px" style={{ color: colors.Grey400 }}>
                    <Box>
                      {(state.currentLaudo)
                        && (
                          <>
                            <StatusText isBold>{t('laudoTecnico')}</StatusText>
                            <StatusText>{state.currentLaudo}</StatusText>
                          </>
                        )}
                      {((state.currentPosCauses || []).length === 1)
                        && (
                          <div>
                            {`${t('possiveisCausas')}: `}
                            {state.currentPosCauses[0]}
                          </div>
                        )}
                      {((state.currentPosCauses || []).length > 1)
                        && (
                          <div>
                            {`${t('possiveisCausas')}: `}
                            {state.currentPosCauses.map((cause) => (
                              <div>
                                -
                                {' '}
                                {cause}
                              </div>
                            ))}
                          </div>
                        )}
                    </Box>
                  </Flex>
                </ContainerHealths>
                {state.currentHealthTitle && (
                  <Flex style={{ flexDirection: 'column' }}>
                    <AnnotationIcon size={24} />
                    <Flex mt={2} mb={3}>
                      {state.feedbackInfo ? t('esteLaudoJaFoiComplementado') : t('gostariaDeComplementarEsseLaudo?')}
                    </Flex>
                    <ButtonCustom
                      width="170px"
                      height="40px"
                      onClick={handleModalOpen}
                    >
                      {state.feedbackInfo ? t('verInformacoes') : t('adicionarInformacoes')}
                    </ButtonCustom>
                  </Flex>
                )}
              </CardWrapper>
            </Box>
            {profile.permissions.HEALTH_MANAGEMENT && (
              <CardWrapper style={{ marginTop: '32px' }}>
                <CardTitle>{t('editarSaude')}</CardTitle>
                <div>
                  <div style={{ paddingBottom: '20px', paddingTop: '20px' }}>
                    <GreenButton selected={state.newHealthIndex === 100} onClick={() => onLevelClick(100)}>{t('corVerde')}</GreenButton>
                    <YellowButton selected={state.newHealthIndex === 75} onClick={() => onLevelClick(75)}>{t('corAmarelo')}</YellowButton>
                    <OrangeButton selected={state.newHealthIndex === 50} onClick={() => onLevelClick(50)}>{t('corLaranja')}</OrangeButton>
                    <RedButton selected={state.newHealthIndex === 25} onClick={() => onLevelClick(25)}>{t('corVermelho')}</RedButton>
                    <GreyButton selected={state.newHealthIndex === 3} onClick={() => onLevelClick(3)}>{t('corCinza')}</GreyButton>
                    <DarkGreyButton selected={state.newHealthIndex === 4} onClick={() => onLevelClick(4)}>{t('desativado')}</DarkGreyButton>
                  </div>
                  {(state.newHealthIndex != null)
                    && (
                      <div>
                        <div style={{ paddingBottom: '20px', color: colors.Grey400 }}>
                          {t('nivel')}
                          {' '}
                          {state.selectedLevelTitle || '?'}
                        </div>
                        {state.laudos.map((item) => (
                          <div>
                            <RadioButton
                              key={item.fullText}
                              label={item.fullText}
                              checked={state.newLaudo === item.fullText}
                              onClick={() => setState({ newLaudo: item.fullText })}
                              style={{ marginTop: '5px' }}
                            />
                          </div>
                        ))}
                        <div>
                          <SaveButton variant="primary" style={{ marginTop: '15px' }} onClick={() => saveHealthInfo()}>{t('salvar')}</SaveButton>
                        </div>
                      </div>
                    )}
                </div>
              </CardWrapper>
            )}
            {(profile.permissions.HEALTH_MANAGEMENT && (state.detectedFaults?.length > 0) && !profile.permissions.isInstaller) && (
              <CardWrapper style={{ marginTop: '32px' }}>
                <CardTitle>{t('falhasDetectadas')}</CardTitle>
                <TContainer>
                  <TableHead>
                    <Row>
                      <THeader>
                        <HeaderTitle>
                          <span>{t('origem')}</span>
                        </HeaderTitle>
                      </THeader>
                      <THeader>
                        <HeaderTitle>
                          <span>{t('gravidade')}</span>
                        </HeaderTitle>
                      </THeader>
                      <THeader>
                        <HeaderTitle>
                          <span>{t('falha')}</span>
                        </HeaderTitle>
                      </THeader>
                      <THeader>
                        <HeaderTitle>
                          <span>&nbsp;</span>
                        </HeaderTitle>
                      </THeader>
                    </Row>
                  </TableHead>
                  <TableBody>
                    {state.detectedFaults.map((item) => (
                      <Row key={item.faultId}>
                        <Data>
                          {item.origem}
                        </Data>
                        <Data>
                          {item.severity}
                        </Data>
                        <Data>
                          {item.faultDesc}
                        </Data>
                        <Data>
                          {item.actionAprove && (
                            <>
                              <ActionButton onClick={() => approveOrRejectSelectedPendingFaults('approve', [{ devId, faultId: item.faultId, state: item.lastAction }])} variant="blue-inv"><AcceptIcon color={colors.LightBlue} /></ActionButton>
                              <ActionButton onClick={() => approveOrRejectSelectedPendingFaults('reject', [{ devId, faultId: item.faultId, state: item.lastAction }])} variant="red-inv"><DeleteOutlineIcon colors={colors.Red} /></ActionButton>
                            </>
                          )}
                          {item.actionTaken || ''}
                        </Data>
                      </Row>
                    ))}
                  </TableBody>
                </TContainer>
              </CardWrapper>
            )}
            {(state.healthHist
              && (
                <CardWrapper style={{ marginTop: '32px' }}>
                  <Flex alignItems="center" justifyContent="space-between" mt="32px">
                    <CardTitle>{t('historicoSaude')}</CardTitle>
                    <Flex alignItems="center" justifyContent="right" mt="32">
                      <BtnExport onClick={getCsvData}>
                        <div>
                          <ExportWorksheet />
                          <Text style={{ paddingLeft: '5px' }}>
                            {t('exportarPlanilha')}
                          </Text>
                        </div>
                      </BtnExport>
                      <CSVLink
                        headers={CSVheader}
                        data={state.csvData}
                        filename={t('historicoDeSaudeDoDispositivosCsv')}
                        separator=";"
                        asyncOnClick
                        enclosingCharacter="'"
                        ref={csvLinkEl}
                      />
                    </Flex>
                  </Flex>
                  <HealthHistoryTable
                    data={state.healthHist}
                    profile={profile}
                    t={t}
                    onDelete={wantDeleteHistoryEvent}
                  />
                </CardWrapper>
              )
            )}

            {((profile.manageAllClients || profile.permissions.isInstaller) && state.observations) && (
              <CardWrapper style={{ marginTop: '32px' }}>
                <Flex alignItems="center" justifyContent="space-between" mt="32px">
                  <CardTitle>{t('observacoes')}</CardTitle>
                  <Flex alignItems="center" justifyContent="right" mt="32">
                    <Button
                      variant="primary"
                      style={{ width: 'fit-content', padding: '6px 15px', backgroundColor: '#363BC4' }}
                      onClick={() => {
                        showAddObservation();
                        wantEditAddProgramming();
                      }}
                    >
                      {t('adicionarObservacao')}
                    </Button>
                  </Flex>
                </Flex>
                <ObservationTable
                  data={state.observations}
                  t={t}
                  onEdit={wantEditAddProgramming}
                  onDelete={wantDeleteObservation}
                />
              </CardWrapper>
            )}

            {state.modalEditObservation && (
              <div style={{ zIndex: 3, position: 'sticky' }}>
                <ModalWindow
                  style={{
                    padding: '0px',
                    marginBottom: 'auto',
                    marginTop: '8%',
                    minWidth: '500px',
                    zIndex: 5,
                  }}
                  onClickOutside={() => {
                    setState({ modalEditObservation: null });
                  }}
                >
                  <Card>
                    <Flex flexDirection="column">
                      <span style={{
                        fontFamily: 'Inter',
                        fontWeight: 700,
                        fontSize: '15px',
                        lineHeight: ' 18px',
                        marginBottom: '18px',
                        color: ' #000',
                      }}
                      >
                        {`${state.modalEditObservation.addEdit === 'Edit' ? t('botaoEditar') : t('adicionar')} ${t('observacao')}`}
                      </span>

                      <span style={{
                        fontFamily: 'Inter',
                        fontWeight: 700,
                        fontSize: '12px',
                        lineHeight: ' 15px',
                        marginBottom: '9px',
                        color: ' #000',
                      }}
                      >
                        {t('descricao')}
                      </span>

                      <TextArea
                        value={descObs}
                        onChange={setDescObs}
                        error={undefined}
                        style={{
                          width: '100%',
                          marginBottom: '26px',
                          padding: '5px',
                        }}
                        maxLength={250}
                      />

                      <Button
                        variant="primary"
                        style={{
                          width: 'fit-content', padding: '6px 15px', backgroundColor: '#363BC4', marginInline: 'auto', marginBottom: '22px',
                        }}
                        onClick={() => {
                          state.modalEditObservation?.addEdit === 'Edit' ? editObservation() : saveNewObservation();
                        }}
                      >
                        {state.modalEditObservation?.addEdit === 'Edit' ? t('salvar') : t('adicionar')}
                      </Button>
                      <CancelButton
                        onClick={() => {
                          setState({ modalEditObservation: null });
                        }}
                      >
                        {t('cancelar')}
                      </CancelButton>
                    </Flex>
                  </Card>
                </ModalWindow>
              </div>
            )}
            {isModalOpen && (
            <FeedbackModal
              isLoading={state.isLoadingModal}
              state={state}
              setState={setState}
              maintenance={maintenance}
              setMaintenance={setMaintenance}
              images={images}
              pdfs={pdfs}
              handleModalClose={handleModalClose}
              handleImageChange={handleImageChange}
              removeImage={removeImage}
              removePdf={removePdf}
              formatPhone={formatPhone}
              submitFeedback={submitFeedback}
              render={render}
            />
            )}
          </Flex>
        )}
    </>
  );
};

const CancelButton = styled.a`
  display: flex;
  justify-content: center;
  font-family: Inter, sans-serif;
  font-size: 13px;
  line-height: 16px;
  color: #202370;
  margin-inline: auto;
  text-decoration-line: underline;
`;
